import axios from "axios";
import urlParse from "url-parse";
import Client from "myinsights-sf-client";
import NitroClient from "@/services/nitro-client";
import SalesDataClient from "@/services/sales-data-client";
import CRMDataEngine from "@/services/crm-data-engine";
import TerritoryFeedbackClient from "@/services/territory-feedback-client";
import DistributionChannelsService from "@/services/distributionChannelsService";
import utils from "@/utils";
import { QUERIES } from "@/static/queries";
import {
  ELM_BLACK_BOX_ERRORS,
  FIELD_TYPE_MAPPING,
  HTML_REPORT_FIELD_NAMES,
  MUTATION_NAME,
  OBJECT_NAME_LABELS,
  SOSL_LIMIT,
  STUDIO_DATA_TYPE,
  UNSUPPORTED_FIELD_TYPE,
  VEEVA_MESSAGE_CATEGORY_QUERY_TEMPLATE,
  VEEVA_MESSAGE_NAME_QUERY_TEMPLATE
} from "@/static/global-variables";
import store from "@/store";
import { MyInsights } from "@/lib/myinsights";
import Bridge from "@/services/myinsights-bridge.js";
import { Elm } from "@/services/Compiler.elm";
import "@/services/Transpiler/template/output/scripts/MyInsightsInterop.js";
import sortBy from "lodash/sortBy";
import Vue from "vue";

const studioDomain = urlParse(location.href).origin;
let nitroClient = null;
let salesDataClient = null;
let crmDataEngine = null;
let MISFClient = null;
let bridge = null;
let elmCompiler = null;
let territoryFeedbackClient = null;

function dataEndpointSuccessHandler(resp) {
  if (resp.data && resp.data.status === "SUCCESS") {
    return resp.data.data;
  } else {
    throw {
      name: "APIRequestError",
      message: "API request was not successful"
    };
  }
}
function dataEndpointErrorHandler(error) {
  return new Promise((resolve, reject) => {
    reject(error);
  });
}

function getLanguage(userLocale) {
  let lang;
  if (userLocale) {
    const langArr = userLocale.split("_");
    lang = langArr.length > 1 ? `${langArr[0]}-${langArr[1]}` : langArr[0];
  }
  return lang || navigator.language;
}

export default {
  studioDomain,
  MISFClient,
  getSession() {
    return axios.get(`${studioDomain}/api/v1/sessions`, { timeout: 60000 });
  },
  addDomainToUrl(domain) {
    axios.defaults.baseURL = `${studioDomain}/${domain}/api`;
  },
  getElmCompiler() {
    return elmCompiler;
  },
  initElmCompiler(userLocale) {
    elmCompiler = Elm.Compiler.init({
      flags: {
        locale: {
          locale: userLocale,
          dateFormat: new Intl.DateTimeFormat(getLanguage(userLocale), {
            dateStyle: "short"
          }).formatToParts(new Date(2021, 0, 1, 0, 0, 0))
        }
      }
    });
    window.ds = new MyInsights(window); // ds must be created for ElmMyInsightsInterop
    window.ElmMyInsightsInterop.init(
      elmCompiler.ports.elmToJsMyInsights,
      elmCompiler.ports.jsToElmMyInsights,
      userLocale
    );
  },

  getCustomSettings(sfEndpoint, sfSession) {
    const options = { headers: { sfEndpoint, sfSession } };

    let promises = [];
    promises.push(
      MISFClient.sfClient.apex.get("/veeva/settings/common", options)
    );
    promises.push(
      MISFClient.sfClient.apex.get("/veeva/settings/territory", options)
    );
    let output = {};

    return Promise.allSettled(promises).then(res => {
      if (res[0].status === "fulfilled") {
        output.crm = utils.extractCommonSettings(
          res[0].value,
          "Veeva_Server_vod__c",
          "Veeva_Version_vod__c"
        );
        output.mc = utils.extractCommonSettings(
          res[0].value,
          "Multichannel_Server_vod__c",
          "Multichannel_Context_Root_vod__c"
        );
      }
      if (res[1].status === "fulfilled") {
        output.territory = utils.extractTerritorySettings(
          res[1].value,
          "Align_Server_vod__c",
          "Align_Version_vod__c"
        );
      }
      return output;
    }, dataEndpointErrorHandler);
  },
  getTerritorySettings(sfEndpoint, sfSession) {
    const options = { headers: { sfEndpoint, sfSession } };

    return MISFClient.sfClient.apex
      .get("/veeva/settings/territory", options)
      .then(res => {
        return {
          territory: utils.extractTerritorySettings(
            res,
            "Align_Server_vod__c",
            "Align_Version_vod__c"
          )
        };
      }, dataEndpointErrorHandler);
  },
  extendSession(params) {
    return axios.put(`${studioDomain}/api/v1/sessions`, null, {
      headers: { sfSession: params.sfSession, sfEndpoint: params.sfEndpoint }
    });
  },
  createApp(params) {
    const data = {
      name: params.name,
      type: params.type,
      description: params.description,
      testRecord: params.testRecord,
      isTemplate: params.isTemplate
    };

    return axios.post("/v1/contents", data);
  },

  validateCustomContentFromFile(file) {
    if (file === null) {
      return false;
    }

    const config = {
      headers: {
        "Content-Type": "multipart/form-data"
      }
    };

    let formData = new FormData();
    formData.append("file", file, file.name);

    return axios.post("/v1/contents/uploads", formData, config);
  },

  createCustomContentWithFile(name, description, file, type) {
    const config = {
      headers: {
        "Content-Type": "multipart/form-data"
      }
    };
    let formData = new FormData();

    const data = {
      name: name,
      description: description,
      customContent: true,
      type: type
    };

    const info = new Blob([JSON.stringify(data)], {
      type: "application/json"
    });

    formData.append("templateFile", file, file.name);
    formData.append("contentInfo", info);

    return axios
      .post("/v2/contents", formData, config)
      .then(dataEndpointSuccessHandler, dataEndpointErrorHandler);
  },

  archiveApp(uuid) {
    return axios.delete(`/v1/contents/${uuid}`);
  },
  async fetchApps(params) {
    const resp = await axios
      .get("/v1/contents", { params })
      .then(dataEndpointSuccessHandler, dataEndpointErrorHandler);
    return sortBy(resp, "name");
  },
  includeQueryFieldsBasedOnFLS(fields, fieldFLS) {
    let output = fields.concat();

    if (fieldFLS) {
      if (fieldFLS.includeProfileName) {
        output.push(HTML_REPORT_FIELD_NAMES.Profile_Name_vod__c);
      }

      if (fieldFLS.includeORTName) {
        output.push(HTML_REPORT_FIELD_NAMES.Object_RecordType_vod__c);
      }

      if (fieldFLS.includeORTId) {
        output.push(HTML_REPORT_FIELD_NAMES.Object_RecordType_Id_vod__c);
      }

      if (fieldFLS.includePlatform) {
        output.push(HTML_REPORT_FIELD_NAMES.Platform_vod__c);
      }

      if (fieldFLS.includePhone) {
        output.push(HTML_REPORT_FIELD_NAMES.Phone_vod__c);
      }

      if (fieldFLS.includeStudioVersion) {
        output.push(HTML_REPORT_FIELD_NAMES.Studio_Version_vod__c);
      }

      if (fieldFLS.includeStatus) {
        output.push(HTML_REPORT_FIELD_NAMES.Status_vod__c);
      }

      if (fieldFLS.includeStatusDetail) {
        output.push(HTML_REPORT_FIELD_NAMES.Failure_Msg_vod__c);
      }

      if (fieldFLS.includeRecordType) {
        output.push(HTML_REPORT_FIELD_NAMES.RecordType);
      }

      if (fieldFLS.includeExternalId) {
        output.push(HTML_REPORT_FIELD_NAMES.External_Id_vod__c);
      }

      if (fieldFLS.includeVisibilityCriteria) {
        output.push(HTML_REPORT_FIELD_NAMES.Visibility_Criteria_vod__c);
      }

      if (fieldFLS.includeSharingGroup) {
        output.push(HTML_REPORT_FIELD_NAMES.Sharing_Group_vod__c);
      }
    }

    return output;
  },
  fetchDeploymentsForApp(uuid, fieldFLS) {
    let config = JSON.parse(JSON.stringify(QUERIES.htmlReport));
    let formattedId = `'${uuid}'`;
    config.fields = this.includeQueryFieldsBasedOnFLS(config.fields, fieldFLS);
    config.where += formattedId;

    return MISFClient.queryObject(config);
  },
  fetchDeploymentsForAll(uuids, fieldFLS) {
    let config = JSON.parse(JSON.stringify(QUERIES.htmlReportDeployments));
    let ids = uuids.map(id => `'${id}'`);
    let inStatement = `(${ids.join(",")})`;

    config.fields = this.includeQueryFieldsBasedOnFLS(config.fields, fieldFLS);
    config.where += inStatement;
    return MISFClient.queryObject(config);
  },
  findRecordsForObject(meta, userInput) {
    if (!userInput || userInput.length < 2) {
      return Promise.resolve({ searchRecords: [] });
    }
    userInput = MISFClient.escapeStringForSOSLAllowWildcards(userInput);

    let config = {
      searchQuery: meta.searchQuery.replace(/userInput/g, userInput),
      searchGroup: meta.searchGroup,
      objectsAndFields: meta.objectsAndFields,
      limit: meta.limit
    };

    if (meta.additionalQuery) {
      return MISFClient.searchObject(config).then(
        resp => this.queryAdditionalObjects(meta, resp),
        error => {
          return Promise.reject(error);
        }
      );
    } else {
      return MISFClient.searchObject(config);
    }
  },
  queryAdditionalObjects(meta, resp) {
    let records = resp.searchRecords;
    let accounts = records.filter(
      record => record.attributes.type === "Account"
    );
    let objects = records.filter(
      record => record.attributes.type === meta.object
    );
    let ids = accounts.map(account => {
      return `'${account.Id}'`;
    });
    let soqlConfig = {
      object: meta.object,
      fields: meta.fields,
      where: meta.where.replace(/ids/g, ids.join(","))
    };
    if (ids.length === 0) {
      let uniqueIds = new Set();
      let uniqObjects = objects.filter(object => {
        let present = uniqueIds.has(object.Id);
        uniqueIds.add(object.Id);
        return !present;
      });
      return { searchRecords: uniqObjects.slice(0, SOSL_LIMIT) };
    } else {
      return MISFClient.queryObject(soqlConfig).then(
        resp => {
          objects = objects.concat(resp.records);
          let uniqueIds = new Set();
          let uniqObjects = objects.filter(object => {
            let present = uniqueIds.has(object.Id);
            uniqueIds.add(object.Id);
            return !present;
          });
          return { searchRecords: uniqObjects.slice(0, SOSL_LIMIT) };
        },
        error => {
          return Promise.reject(error);
        }
      );
    }
  },
  getMessages(userLocale, messages) {
    return MISFClient.getVeevaMessages(userLocale, messages).then(
      resp => {
        let missingMessages = utils.findMissingVeevaMessages(
          resp.Message_vod__c,
          messages
        );

        if (missingMessages.length > 0) {
          return MISFClient.getVeevaMessages("en_US", missingMessages).then(
            function(res) {
              return new Promise(resolve => {
                resolve(resp.Message_vod__c.concat(res.Message_vod__c));
              });
            },
            error => {
              return new Promise((resolve, reject) => {
                reject(error);
              });
            }
          );
        } else {
          return new Promise(resolve => {
            resolve(resp.Message_vod__c);
          });
        }
      },
      err => {
        return new Promise((resolve, reject) => {
          reject(err);
        });
      }
    );
  },
  async fetchObjectNames() {
    const promises = [];
    const objNames = {};
    OBJECT_NAME_LABELS.forEach(e => {
      promises.push(
        MISFClient.getObjectLabels([e])
          .then(result => {
            objNames[e] = result[e];
          })
          .catch(err => {
            console.error(`${e} ${err.name} ${err.message}`);
            objNames[e] = null;
          })
      );
    });
    await Promise.all(promises);
    return objNames;
  },
  async fetchObjectOLS() {
    if (!store.getters.objectLevelSecurity) {
      const promises = [];
      const objNames = {};
      OBJECT_NAME_LABELS.concat([QUERIES.htmlReport.object]).forEach(e => {
        promises.push(
          this.describeObject(e)
            .then(result => {
              objNames[e] = result;
            })
            .catch(err => {
              console.error(`${e} ${err.name} ${err.message}`);
              objNames[e] = null;
            })
        );
      });
      await Promise.all(promises);
      store.commit(MUTATION_NAME.SET_OLS, objNames);
      return objNames;
    } else {
      return store.getters.objectLevelSecurity;
    }
  },
  fetchFieldLabelsForObject(objName) {
    return MISFClient.getFieldLabels({ object: objName });
  },
  fetchAppTypes() {
    return MISFClient.queryObject(QUERIES.htmlRecordTypes);
  },
  setAPICommonHeaders(sfSession, sfEndpoint) {
    axios.defaults.headers.common["sfSession"] = sfSession;
    axios.defaults.headers.common["sfEndpoint"] = sfEndpoint;
  },
  instantiateSFClient(sfSession, sfEndpoint) {
    MISFClient = new Client(sfEndpoint, sfSession);
  },
  instantiateBridge(info) {
    if (!bridge) {
      const sfEndpoint = Vue.$cookies.get("sfEndpoint");
      const sfSession = Vue.$cookies.get("sfSession");
      const orgId = store.getters.org.id || "";
      salesDataClient = new SalesDataClient(
        store.getters.crmServerURL,
        store.getters.crmServerVersion,
        sfEndpoint
      );
      crmDataEngine = new CRMDataEngine(
        store.getters.crmServerURL,
        store.getters.crmServerVersion,
        sfEndpoint,
        sfSession,
        orgId
      );
      nitroClient = new NitroClient(
        store.getters.crmServerURL,
        store.getters.crmServerVersion,
        sfEndpoint,
        orgId
      );
      territoryFeedbackClient = new TerritoryFeedbackClient(
        store.getters.territoryServerURL,
        sfEndpoint,
        sfSession,
        info.User,
        info.Test_Record
      );
      bridge = new Bridge(
        MISFClient,
        info,
        nitroClient,
        salesDataClient,
        crmDataEngine,
        territoryFeedbackClient
      );
    } else {
      bridge.setCurrentObjectIds(info);
    }
    elmCompiler.ports.rxFromFrontEnd.send({
      type: "PurgeAllDataElementsAndManifests"
    });
  },
  subscribeToIsDeployable() {
    elmCompiler.ports.sendToFrontEnd.subscribe(resp => {
      let value;

      if (resp.success && resp.type === "ConfigValidationResponse") {
        value = true;
      } else {
        value = false;
        if (Array.isArray(resp.errors)) {
          let manifestsNotFounds = resp.errors.filter(e => {
            return e.type === ELM_BLACK_BOX_ERRORS.CANNOT_FIND_MANIFEST;
          });
          if (manifestsNotFounds.length > 0) {
            store.commit(MUTATION_NAME.SET_IS_FETCHING_MANIFESTS, true);
            let manifests = [];
            const promises = manifestsNotFounds.map(obj => {
              return DistributionChannelsService.widgetVersionDetails(
                obj.widgetUuid,
                obj.versionUuid
              );
            });

            Promise.all(promises)
              .then(result => {
                manifests = result.map((r, i) => {
                  return {
                    widgetUuid: manifestsNotFounds[i].widgetUuid,
                    versionUuid: manifestsNotFounds[i].versionUuid,
                    manifest: r.details.manifest
                  };
                });
                elmCompiler.ports.rxFromFrontEnd.send({
                  type: "RxManifests",
                  manifests: manifests
                });
              })
              .catch(err => {
                console.error(err);
              })
              .finally(() => {
                store.commit(MUTATION_NAME.SET_IS_FETCHING_MANIFESTS, false);
              });
          }
        }
      }

      const currentContent = store.getters.currentContent;

      if (currentContent) {
        currentContent.isDeployable = value;
        store.commit(MUTATION_NAME.SET_CURRENT_CONTENT, currentContent);
      }
    });
  },
  saveAppsLocally(apps) {
    localStorage.setItem("apps", JSON.stringify(apps));
  },
  getAppsFromLocal() {
    return JSON.parse(localStorage.getItem("apps"));
  },
  mergeDirtyApps(fetchedApps) {
    let merged = fetchedApps;
    const localApps = this.getAppsFromLocal();
    const dirtyApps = localApps ? localApps.filter(app => app.isDirty) : [];
    if (dirtyApps.length) {
      merged = fetchedApps.map(app => {
        let dirtyA = dirtyApps.find(dirtyApp => dirtyApp.uuid === app.uuid);
        if (dirtyA) {
          dirtyA.isTemplate = app.isTemplate;
          dirtyA.version = app.version;
        }

        return dirtyA || app;
      });
    }
    return merged;
  },
  describeObject(objName) {
    const TEXTAREA_TEXT = "textarea";

    return MISFClient.sfClient.describe(objName).then(resp => {
      resp.fields.forEach(field => {
        if (UNSUPPORTED_FIELD_TYPE.has(field.type)) {
          field.hideFromWhereClause = true;
          field.hideFromMultiSelect = true;
        }

        if (field.type === TEXTAREA_TEXT && field.length > 255) {
          // filter out long text area since soql does not support it
          field.hideFromWhereClause = true;
        }
        field.originalType = field.type;
        field.type = FIELD_TYPE_MAPPING[field.type] || field.type;
      });
      return resp;
    }, dataEndpointErrorHandler);
  },
  describeObjects(objectNames) {
    let promises = [];
    let output = {};

    objectNames.forEach(name => {
      promises.push(MISFClient.sfClient.describe(name));
    });

    return Promise.all(promises).then(responses => {
      responses.forEach(resp => {
        output[resp.label] = resp;
        output[resp.label].fields = output[resp.label].fields.map(field => {
          return {
            label: field.label,
            name: field.name,
            type: FIELD_TYPE_MAPPING[field.type] || field.type,
            originalType: field.type
          };
        });
      });

      return output;
    }, dataEndpointErrorHandler);
  },
  fetchAvailableObjects() {
    return MISFClient.sfClient.describeGlobal().then(function(res) {
      return res.sobjects
        .filter(obj => obj.queryable)
        .map(obj => {
          return {
            name: obj.name,
            label: obj.label
          };
        });
    }, dataEndpointErrorHandler);
  },

  fetchAvailableTables() {
    const sfSession = Vue.$cookies.get("sfSession");
    const sfEndpoint = Vue.$cookies.get("sfEndpoint");

    const url = `${store.getters.crmServerURL}/${store.getters.crmServerVersion}/api/v1/vds/metadata`;

    return axios
      .get(url, {
        headers: { sfSession, sfEndpoint: sfEndpoint }
      })
      .then(resp => {
        if (resp.data && resp.data.status === "SUCCESS") {
          return utils.reformatNitroTable(resp.data.data.content);
        } else {
          throw {
            name: "APIRequestError",
            message: "API request was not successful"
          };
        }
      }, dataEndpointErrorHandler);
  },

  describeHTMLReportObject() {
    return MISFClient.sfClient.describe(QUERIES.htmlReport.object);
  },
  fetchProfiles() {
    return MISFClient.queryObject(QUERIES.profiles).then(resp => {
      return resp.records.map(record => {
        record.value = record.Id;
        record.label = record.Name;
        record.name = record.Name;
        return record;
      });
    }, console.error);
  },
  fetchObjectRecordTypes(sObjectName) {
    let config = JSON.parse(JSON.stringify(QUERIES.objectRecordTypes));

    config.where = config.where.replace("%obj_name%", sObjectName);
    return MISFClient.queryObject(config).then(resp => {
      return resp.records.map(record => {
        record.value = record.Id;
        record.label = record.Name;
        record.name = record.Name;
        return record;
      });
    }, console.error);
  },
  fetchVersions(appId) {
    return axios
      .get(`/v1/contents/${appId}/versions`, null)
      .then(dataEndpointSuccessHandler, dataEndpointErrorHandler);
  },
  fetchVersionMatch(appId) {
    return axios
      .get(`/v1/contents/${appId}/deployments`, null)
      .then(dataEndpointSuccessHandler, dataEndpointErrorHandler);
  },
  doDeploy(appId, version, toDeploy) {
    let body = {
      actions: toDeploy
    };

    return axios.post(
      `/v1/contents/${appId}/versions/${version}/deployments`,
      body
    );
  },
  fetchTestRecordInfo(config, id) {
    const clone = JSON.parse(JSON.stringify(config));
    clone.where = `Id = '${id}'`;

    return MISFClient.queryObject(clone).then(
      resp => resp.records,
      console.error
    );
  },

  updateAppSetting(params) {
    let {
      name,
      description,
      appId,
      record,
      isTemplate,
      sourceTemplateUuid,
      sourceTemplateVersion,
      version
    } = params;
    let config = {
      name,
      description,
      isTemplate,
      sourceTemplateUuid,
      sourceTemplateVersion
    };

    if (record) {
      if (typeof record === "string") {
        config.testRecord = record;
      } else {
        delete record.attributes;
        config.testRecord = record.Id;
      }
    }

    if (version) {
      config.version = version;
    }

    return axios.put(`/v1/contents/${appId}/metadata`, config);
  },
  updateElmCompiler(data) {
    // removing all undefined properties in the object
    const formatted = JSON.parse(JSON.stringify(data));
    elmCompiler.ports.rxFromFrontEnd.send({
      type: "ConfigChange",
      app: formatted
    });
  },
  saveApp(appData, updateVersion, isDeployable) {
    const clone = JSON.parse(JSON.stringify(appData));
    if (clone.name) {
      delete clone.name;
    }

    delete clone.sourceTemplateUuid;
    delete clone.sourceTemplateVersion;
    delete clone.isTemplate;
    delete clone.version;

    let body = {
      json: clone,
      updateVersion: updateVersion,
      deployable: !!isDeployable
    };

    return axios.put(`/v1/contents/${clone.uuid}`, body);
  },
  fetchObjectMetaData(object) {
    const url = `/query?q=Select DataType, QualifiedApiName, Description from FieldDefinition Where EntityDefinition.QualifiedApiName='${object}' AND DataType != 'Address'`;

    return MISFClient.sfClient.request(url);
  },

  fetchCurrentPreviewRecord(object, recordId) {
    const config = JSON.parse(
      JSON.stringify(utils.getRecordTypeObject(object))
    );
    config.where = `Id = '${recordId}'`;

    return MISFClient.queryObject(config).then(
      resp => resp.records[0],
      console.error
    );
  },

  fetchSelectedApp(params) {
    const { appId, version } = params;
    const targetVersion = version ? version : "latest";

    return axios
      .get(`/v1/contents/${appId}/versions/${targetVersion}`, null)
      .then(dataEndpointSuccessHandler, dataEndpointErrorHandler);
  },

  fetchContentContextData(objName, recordId) {
    const fields = objName === "Account" ? ["Id"] : ["Id", "Account_vod__c"];
    const config = {
      object: objName,
      fields,
      where: `Id = '${recordId}'`
    };

    return MISFClient.queryObject(config);
  },

  fetchVeevaMsgCategoriesOnly(userLanguage, categoryInput) {
    const template = VEEVA_MESSAGE_CATEGORY_QUERY_TEMPLATE;
    let where = template.where;
    where = where.replace("userLanguage", userLanguage);
    where = where.replace(
      "categoryInput",
      utils.escapeSingleQuotes(categoryInput)
    );
    const config = {
      limit: template.limit,
      object: template.object,
      fields: template.fields,
      where
    };
    return MISFClient.queryObject(config);
  },

  fetchVeevaMsgNames(userLanguage, categoryInput, nameInput) {
    const template = VEEVA_MESSAGE_NAME_QUERY_TEMPLATE;
    let where = template.where;
    where = where.replace("userLanguage", userLanguage);
    where = where.replace(
      "categoryInput",
      utils.escapeSingleQuotes(categoryInput)
    );
    where = where.replace("msgNameInput", utils.escapeSingleQuotes(nameInput));
    const config = {
      limit: template.limit,
      object: template.object,
      fields: template.fields,
      where
    };

    return MISFClient.queryObject(config);
  },
  publishContent(reportId) {
    const sfSession = Vue.$cookies.get("sfSession");
    const sfEndpoint = Vue.$cookies.get("sfEndpoint");

    const url = `${store.getters.mcServerURL}/${store.getters.mcServerVersion}/api/v1/htmlreports/actions/publish`;

    let body = {
      htmlReportId: reportId
    };

    return axios.post(url, body, {
      headers: { sfSession, sfEndpoint: sfEndpoint }
    });
  },
  checkActionPlanStatus() {
    const sfSession = Vue.$cookies.get("sfSession");
    const sfEndpoint = Vue.$cookies.get("sfEndpoint");

    let config = {
      method: "get",
      maxBodyLength: Infinity,
      url: "/v1/contents/import-jobs",
      headers: {
        sfSession,
        sfEndpoint: sfEndpoint
      }
    };
    return axios(config).then(
      dataEndpointSuccessHandler,
      dataEndpointErrorHandler
    );
  },
  fetchActionPlan() {
    const sfSession = Vue.$cookies.get("sfSession");
    const sfEndpoint = Vue.$cookies.get("sfEndpoint");

    let config = {
      method: "post",
      maxBodyLength: Infinity,
      url: `/v1/contents/import-jobs?isAdhoc=true&isCanceled=false`,
      headers: {
        sfSession,
        sfEndpoint: sfEndpoint
      }
    };

    return axios(config).then(
      dataEndpointSuccessHandler,
      dataEndpointErrorHandler
    );
  },
  submitActionPlan(actionPlan) {
    const sfSession = Vue.$cookies.get("sfSession");
    const sfEndpoint = Vue.$cookies.get("sfEndpoint");

    let config = {
      method: "post",
      maxBodyLength: Infinity,
      url: `/v1/contents/import-jobs?isAdhoc=true&isCanceled=false`,
      headers: {
        sfSession,
        sfEndpoint: sfEndpoint,
        "Content-Type": "application/json"
      },
      data: JSON.stringify(actionPlan)
    };
    return axios(config).then(
      dataEndpointSuccessHandler,
      dataEndpointErrorHandler
    );
  },
  abandonActionPlan(actionPlan) {
    const sfSession = Vue.$cookies.get("sfSession");
    const sfEndpoint = Vue.$cookies.get("sfEndpoint");

    let config = {
      method: "post",
      maxBodyLength: Infinity,
      url: `/v1/contents/import-jobs?isAdhoc=true&isCanceled=true`,
      headers: {
        sfSession,
        sfEndpoint: sfEndpoint,
        "Content-Type": "application/json"
      },
      data: JSON.stringify(actionPlan)
    };
    return axios(config).then(
      dataEndpointSuccessHandler,
      dataEndpointErrorHandler
    );
  },
  async updateCustomContentFile(params) {
    // fetch previous versions
    let appId = params.uuid;
    let version = null;
    axios.get(`/v1/contents/${appId}/versions`).then(resp => {
      if (
        resp.data &&
        resp.data.status &&
        Array.isArray(resp.data.data) &&
        resp.data.data.length > 0
      ) {
        version = resp.data.data.slice(-1)[0].version;
      } else {
        throw {
          name: "APIRequestError",
          message: "API request was not successful"
        };
      }
    }, dataEndpointErrorHandler);

    // fetch previous versioned content
    let fetchSelectedAppParams = {
      appId: appId,
      version: version
    };
    let previousApp = await this.fetchSelectedApp(fetchSelectedAppParams);

    const config = {
      headers: {
        "Content-Type": "multipart/form-data"
      }
    };

    const previousAppJson = new Blob([JSON.stringify(previousApp)], {
      type: "application/json"
    });

    let formData = new FormData();

    formData.append("file", params.file, params.file.name);
    formData.append("versionedContent", previousAppJson);

    return axios
      .put(`/v2/contents/${params.uuid}`, formData, config)
      .then(dataEndpointSuccessHandler, dataEndpointErrorHandler);
  },
  getTablesFromCRMDataEngine() {
    return window.ds.getDataEngineTables();
  },
  getFieldsFromTable(name) {
    return window.ds.getDataEngineTableMetadata(name).then(resp => {
      if (resp.success) {
        return resp.data.map(r => {
          r.originalType = r.dataType;
          r.label = r.name;
          r.type = FIELD_TYPE_MAPPING[r.dataType.toLowerCase()];
          if (r.type === STUDIO_DATA_TYPE.DATE_TIME) {
            r.hideFromWhereClause = true;
          }
          return r;
        });
      } else {
        return [];
      }
    });
  },
  getFeedbackMetadata() {
    return window.ds.getFeedbackMetadata().then(resp => {
      return resp.terrMetadata;
    });
  },
  filterObjectFields(metadata) {
    let fields = [];
    for (const [key, value] of Object.entries(metadata)) {
      if (value.type !== "object" && value.type !== "array") {
        fields.push({
          name: key,
          type: this.fieldTypeFromSchema(value)
        });
      }
    }
    return fields;
  },
  fieldTypeFromSchema(value) {
    let result = value.type;
    if (Array.isArray(value.type)) {
      value.type.filter(val => {
        return val !== "null";
      });
      result = value.type.length > 0 ? value.type[0] : "null"; //assuming value will only have one not null type
    }
    if (value.format) {
      if (value.format === "date-time") {
        return "datetime";
      }
      return value.format;
    }
    return result;
  },
  getProductMetricFields(metricData) {
    let fields = [];
    metricData.oneOf.forEach(metricObj => {
      metricObj.properties.metric.enum.forEach(metricField => {
        const fieldObj = Object.values(metricObj.properties.productMetricValues.properties);
        fields.push({
          name: metricField,
          label: metricData.fieldToLabel[metricField],
          type: this.fieldTypeFromSchema(fieldObj[0])
        });
      });
    });
    return fields;
  },
  fieldNameToTitleCaseLabel(fields) {
    fields.forEach(field => {
      field.label = this.camelCaseToTitleCase(
        field.label ? field.label : field.name
      );
    });
    return fields;
  },
  camelCaseToTitleCase(str) {
    return str
      .replace(/[0-9]{2,}/g, match => ` ${match} `)
      .replace(/[^A-Z0-9][A-Z]/g, match => `${match[0]} ${match[1]}`)
      .replace(
        /[A-Z][A-Z][^A-Z0-9]/g,
        match => `${match[0]} ${match[1]}${match[2]}`
      )
      .replace(/[ ]{2,}/g, " ")
      .replace(/\s./g, match => match.toUpperCase())
      .replace(/^./, match => match.toUpperCase())
      .trim();
  },
  updateChannelIDLabel(goals) {
    goals.forEach(field => {
      if (field.name === "channelOrProductId") {
        field.label = "channelID";
      }
    });
  },
  updateProductIDLabel(goals) {
    goals.forEach(field => {
      if (field.name === "channelOrProductId") {
        field.label = "productID";
      }
    });
  }
};
